import { LinkedProjectConfig, ServiceTransaction } from '../types';
import {
    ersetzeJournalzeilen,
    ladeLebendeJournalzeilenZu,
    lebendeZeilenZu,
    loadJournal,
    meldeSpiegelFehler,
    mirrorEntriesToJournalOhneKorb,
    toJournalRows,
    voidJournalEntries,
    type ServiceJournalRow,
} from './serviceJournal';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE JOURNAL-HAELFTE VON „ZUORDNUNG KORRIGIEREN" (moveAltBackfill)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Umzug haengt ALT-Protokollzeilen von einer Position auf eine andere und
 * kuerzt sie ggf. auf die Menge, die die Quelle noch hergeben kann
 * (`appliedQuantity`). Im JSON ist das ein Update derselben Zeile — das
 * Journal kennt kein Update. Die gespiegelte Zeile traegt die Position als
 * Spalte und muss durch eine NEUE ersetzt werden, sonst steht sie weiter auf
 * der Quelle (6290-1, 02.09.2026: das Journal zeigte auf eine laengst
 * geloeschte Altposition).
 *
 * ─── Was bis 02.10.2026 hier stand, und warum es falsch war ──────────────
 * `AdminAccountingView.handleMoveAltBackfill` spiegelte in ZWEI Aufrufen:
 * `mirrorEntriesToJournal` (mit Ausgangskorb) und danach
 * `voidJournalEntries` mit den JSON-ids. Drei Fehler:
 *   1. Zwei Aufrufe — jeder Abbruch dazwischen hinterlaesst einen
 *      Halbzustand, und der Korb konnte die neue Zeile Stunden spaeter
 *      nachliefern, neben eine Zeile, die niemand mehr stilllegt.
 *   2. Stilllegen ueber die JSON-id trifft bei zwei gleichrangigen lebenden
 *      Zeilen die falsche — auch die gerade angelegte Ersatzzeile, denn deren
 *      `legacy_entry_id` IST die JSON-id (Vorfall 5633, 29.09.2026).
 *   3. A→B→A→B: der abgeleitete Schluessel der Ersatzzeile (md5 ueber den
 *      Inhalt, also auch ueber die Position) ist beim dritten Umzug derselbe
 *      wie beim ersten und laengst STILLGELEGT. Das Anhaengen schreibt nichts
 *      (`on conflict do nothing`), das Stilllegen danach nimmt die lebende
 *      Zeile weg — KEINE lebende Zeile mehr (Fall 6263).
 *
 * Richtig ist dasselbe Muster wie bei der teilweisen Ruecknahme
 * (`serviceReversalService.spiegleGekuerzteZeile`): die alten Zeilen ueber
 * ihre JOURNAL-ids, mit erwartetem Stand, in EINER Transaktion stilllegen und
 * die Ersatzzeile einfuegen (`ersetzeJournalzeilen`, SQL Nr. 136). Scheitert
 * das, bleibt die alte Zeile lebend auf der Quellposition — ein falscher Ort,
 * aber keine verlorene Menge; der Zuordnungs-Schatten im Nachtlauf zeigt sie.
 */

export interface ZuordnungsUmzugSpiegeln {
    projectId: string;
    /** Protokoll VOR dem Umzug (globalData.serviceLog, wie geladen). Liefert erwartete Menge + vergebene ids. */
    vorher: ServiceTransaction[];
    /** Protokoll NACH dem Umzug (result.serviceLog, wie gespeichert). */
    nachher: ServiceTransaction[];
    /** result.movedEntryIds — JSON-ids, Protokollreihenfolge. */
    movedEntryIds: string[];
    linkedProjectConfig: LinkedProjectConfig | null;
}

export interface ZuordnungsUmzugErgebnis {
    /** JSON-ids, deren Journalzeilen ueber Nr. 136 ersetzt wurden (inkl. unveraendert). */
    ersetzt: string[];
    /** JSON-ids ohne lebende Zeile, nur angehaengt. */
    angehaengt: string[];
    /** JSON-ids ueber den Zweischritt-Rueckfall (PGRST202). */
    rueckfall: string[];
    /** Nichts geschrieben. */
    uebersprungen: Array<{ id: string; grund: string }>;
}

/** Rundungsgrenze der Mengen (drei Stellen, wie `round3` der Logik). */
const MENGEN_TOLERANZ = 0.0005;

/**
 * SQLSTATE 55000 (`object_not_in_prerequisite_state`) aus Nr. 136: der Stand
 * im Journal passt nicht zur Erwartung, oder die Ersatzzeile ist schon
 * stillgelegt. Nachgebaut, weil die Fassung im Reversal nicht exportiert ist.
 */
const istZustandGeaendert = (error: unknown): boolean =>
    String((error as { code?: unknown } | null | undefined)?.code ?? '') === '55000';

const kennung = (e: ServiceTransaction | null | undefined): string => String(e?.id ?? '').trim();

type Ausgang =
    | { art: 'ersetzt' | 'angehaengt' | 'rueckfall' }
    | { art: 'uebersprungen'; grund: string };

const uebersprungen = (grund: string): Ausgang => ({ art: 'uebersprungen', grund });

/**
 * Spiegelt einen bestaetigt gespeicherten Zuordnungsumzug ins Journal.
 *
 * JE JSON-id ein eigener Vorgang, in der Reihenfolge von `movedEntryIds`: ein
 * Fehlschlag (auch ein Wurf) haelt die uebrigen nicht auf. Eine Rechnung mit
 * drei Alt-Zeilen, von denen eine im Journal einen fremden Stand traegt, soll
 * die anderen zwei trotzdem richtig umhaengen.
 *
 * Doppelte Nennungen in `movedEntryIds` gehen nur einmal durch: eine doppelte
 * id im Protokoll wird ohnehin an der Eindeutigkeit abgewiesen, und eine
 * zweite Runde haette nur eine zweite Meldung derselben Sache geschrieben.
 *
 * Das Journal wird fuer ALLE ids EINMAL gelesen (Review 02.10.2026): bis dahin
 * holte jede umgezogene Zeile das ganze Projektjournal seitenweise neu — bei
 * einer Rechnung mit zwanzig Alt-Zeilen zwanzigmal dasselbe. Gelesen wird erst,
 * wenn die erste id es braucht; scheitern alle schon an der Eindeutigkeit,
 * bleibt das Journal ungelesen.
 *
 * Wirft nie: das JSON ist gespeichert, der Vorgang ist gelungen. Nichts davon
 * gehoert in einen Dialog — eine Restabweichung zeigt der Nachtlauf.
 */
export const spiegleZuordnungsUmzug = async (args: ZuordnungsUmzugSpiegeln): Promise<ZuordnungsUmzugErgebnis> => {
    const ergebnis: ZuordnungsUmzugErgebnis = { ersetzt: [], angehaengt: [], rueckfall: [], uebersprungen: [] };
    const ids = [...new Set((args?.movedEntryIds ?? []).map((v) => String(v ?? '').trim()))];
    // Der Stand VOR dem Spiegeln, einmal gelesen. Er bleibt fuer die spaeteren
    // ids gueltig: das Ersetzen einer id beruehrt nur die Zeilen, die auf SIE
    // antworten (die Ersatzzeile traegt ihre JSON-id in `legacy_entry_id`),
    // und Nr. 136 prueft den erwarteten Stand ohnehin noch einmal unter der
    // Zeilensperre. `null` = nicht lesbar — dann wird jede id uebersprungen.
    let gelesen: Promise<ServiceJournalRow[] | null> | null = null;
    const journalVorher = () => (gelesen ??= loadJournal(args?.projectId ?? ''));
    for (const id of ids) {
        let ausgang: Ausgang;
        try {
            ausgang = await spiegleEineZeile(args, id, journalVorher);
        } catch (e) {
            console.warn(
                `[backfillMoveJournal] Umgezogene Zeile ${id || '(ohne id)'} NICHT im Journal gespiegelt — unerwarteter Fehler. `
                + 'Die alte Journalzeile bleibt lebend auf der Quellposition; die übrigen Zeilen laufen weiter.',
                e,
            );
            ausgang = uebersprungen(`unerwarteter Fehler: ${e instanceof Error ? e.message : String(e)}`);
        }
        if (ausgang.art === 'uebersprungen') ergebnis.uebersprungen.push({ id, grund: ausgang.grund });
        else ergebnis[ausgang.art].push(id);
    }
    return ergebnis;
};

const spiegleEineZeile = async (
    args: ZuordnungsUmzugSpiegeln,
    id: string,
    journalVorher: () => Promise<ServiceJournalRow[] | null>,
): Promise<Ausgang> => {
    const { projectId, linkedProjectConfig } = args;
    const vorher = args.vorher ?? [];
    const nachher = args.nachher ?? [];

    // ── Erst die Eindeutigkeit ──────────────────────────────────────────────
    // Steht die id vorher oder nachher mehr als einmal, antworten die lebenden
    // Journalzeilen auf ZWEI JSON-Zeilen; ersetzt wuerden beide, eingefuegt nur
    // eine. `moveAltBackfill` haengt bei einer doppelten id sogar JEDE Zeile mit
    // dieser id um, auch eine fremde. Fehlt sie nachher, ist unklar, was
    // gespeichert wurde. In allen drei Faellen: nichts schreiben, melden.
    const vorherTreffer = vorher.filter((e) => kennung(e) === id);
    const nachherTreffer = nachher.filter((e) => kennung(e) === id);
    if (!id || vorherTreffer.length !== 1 || nachherTreffer.length !== 1) {
        const grund = vorherTreffer.length > 1 || nachherTreffer.length > 1
            ? `die id ${id} steht mehrfach im Protokoll (vorher ${vorherTreffer.length}, nachher ${nachherTreffer.length}) `
              + '— Quarantäneklasse doppelt_im_projekt'
            : `die id ${id || '(ohne id)'} steht nicht genau einmal im Protokoll `
              + `(vorher ${vorherTreffer.length}, nachher ${nachherTreffer.length})`;
        console.warn(
            `[backfillMoveJournal] Umgezogene Zeile NICHT im Journal gespiegelt: ${grund}. `
            + 'Die alte Journalzeile bleibt lebend auf der Quellposition, der Nachtlauf zeigt sie.',
        );
        void meldeSpiegelFehler(projectId, [], new Error(`Zuordnungsumzug nicht gespiegelt: ${grund}`), 'Ersetzen');
        return uebersprungen(grund);
    }

    // Die Menge VOR dem Umzug ist der erwartete Stand der alten Journalzeile;
    // die Zeile NACH dem Umzug traegt die neue Position und ggf. die
    // gedeckelte Menge (auch 0 — sie bleibt als Beleg stehen).
    const mengeVorher = Number(vorherTreffer[0].quantity);
    const positionVorher = String(vorherTreffer[0].serviceId ?? '').trim();
    const neu = nachherTreffer[0];

    // `knownIds` aus dem Protokoll VORHER: die JSON-id ist im Journal
    // vergeben, die Ersatzzeile bekommt deshalb einen abgeleiteten Schluessel
    // und traegt die JSON-id in `legacy_entry_id` — die Zuordnungen der
    // Rechnungen finden ihren Eintrag ueber sie weiter.
    const knownIds = vorher.map(kennung).filter(Boolean);
    const zeilen = toJournalRows({ projectId, entries: [neu], source: 'admin_accounting', linkedProjectConfig, knownIds });
    if (zeilen.length === 0) {
        const grund = `die umgezogene Zeile ${id} ist nicht abbildbar (Position, Menge oder Datum fehlt)`;
        console.warn(
            `[backfillMoveJournal] Umgezogene Zeile NICHT im Journal gespiegelt: ${grund}. `
            + 'Die alte Journalzeile bleibt lebend auf der Quellposition.',
        );
        void meldeSpiegelFehler(projectId, [], new Error(`Zuordnungsumzug nicht gespiegelt: ${grund}`), 'Ersetzen');
        return uebersprungen(grund);
    }
    const neueId = zeilen[0].id;

    // War das Journal nicht lesbar, geschieht nichts: ohne zu wissen, was
    // lebt, liesse sich weder ersetzen noch gefahrlos anhaengen.
    const journal = await journalVorher();
    if (!journal) {
        console.warn(`[backfillMoveJournal] Journal nicht lesbar — umgezogene Zeile ${id} nicht gespiegelt, der Nachtlauf zeigt sie.`);
        return uebersprungen('Journal nicht lesbar');
    }
    const alte = lebendeZeilenZu(journal, id);

    // Die Ersatzzeile selbst gehoert nie in die Liste der alten: die Funktion
    // wiese „dieselbe id alt und neu" als Widerspruch ab.
    const zuErsetzen = alte.filter((z) => z.id !== neueId);
    if (zuErsetzen.length === 0) {
        // Der Zielstand steht schon (Wiederholung, oder ein frueherer Lauf
        // ist durchgekommen) — nichts zu tun.
        if (alte.some((z) => z.id === neueId)) return { art: 'ersetzt' };
        // Nie gespiegelt: nur anhaengen. OHNE Korb — die Antwort muss
        // endgueltig sein; kaeme die Zeile spaeter aus dem Korb nach, etwa
        // nach einem Rueckumzug, stuende sie neben der dann lebenden.
        const angehaengt = await mirrorEntriesToJournalOhneKorb({
            projectId,
            entries: [neu],
            previousLog: vorher,
            source: 'admin_accounting',
            linkedProjectConfig,
        });
        if (!angehaengt.written) {
            console.warn(
                `[backfillMoveJournal] Umgezogene Zeile ${id} ohne lebende Journalzeile konnte nicht angehängt werden.`,
                angehaengt.reason,
                angehaengt.error,
            );
            return uebersprungen(`Anhängen gescheitert (${angehaengt.reason ?? 'unbekannt'})`);
        }
        // `written` heisst nur „die RPC hat nicht geworfen" (Review 02.10.2026,
        // Opus P3-2): ist der Ersatzschluessel schon stillgelegt — ein Rest von
        // 6263, der noch einmal umzieht —, schreibt `on conflict do nothing`
        // nichts. Schaden entsteht keiner (vorher wie nachher keine lebende
        // Zeile), aber ohne Nachlesen stuende hier „angehaengt" ohne Meldung.
        // Ist das Journal jetzt nicht lesbar, bleibt es beim Erfolg der RPC.
        // FRISCH gelesen, nicht aus dem Stand vorher: gefragt ist, was das
        // Anhaengen eben geschrieben hat.
        const danach = await ladeLebendeJournalzeilenZu(projectId, id);
        if (danach.journalGelesen && !danach.zeilen.some((z) => z.id === neueId)) {
            const grund = `die Ersatzzeile ${neueId} lebt nach dem Anhängen nicht (Schlüssel schon stillgelegt)`;
            console.warn(
                `[backfillMoveJournal] Umgezogene Zeile ${id} ohne lebende Journalzeile: ${grund}. `
                + 'Zu diesem Eintrag lebt keine Journalzeile, der Nachtlauf zeigt sie.',
            );
            void meldeSpiegelFehler(projectId, zeilen, new Error(`Zuordnungsumzug nicht gespiegelt: ${grund}`), 'Ersetzen');
            return uebersprungen(grund);
        }
        return { art: 'angehaengt' };
    }

    // ── Der erwartete alte Stand, VOR dem Aufruf ────────────────────────────
    // Jede zu ersetzende Zeile muss MENGE UND POSITION vor dem Umzug tragen.
    // Sonst hat ein anderer Vorgang das Journal geaendert (oder ein frueherer
    // Fehlschlag hat einen gemischten Stand hinterlassen) — dann weder ersetzen
    // noch anhaengen, denn welcher Stand richtig ist, weiss diese Stelle nicht.
    // Nur melden.
    //
    // Die Position gehoert dazu (Codex-Review 02.10.2026, P2): Nr. 136 prueft
    // nur die Menge. Zwei Umzuege derselben Zeile kurz nacheinander — A→B,
    // dann B→C, gleiche Menge —, und die Spiegelung von B→C ist zuerst durch:
    // die verspaetete von A→B laede die lebende C-Zeile, ihre Menge passte,
    // und das Journal stuende auf B, waehrend das JSON C traegt. Der Preis: eine
    // Zeile, die im Journal schon auf der falschen Position steht (Rest einer
    // frueheren Drift, Typ 6290-1, oder ein 55000 nach A→B→A→B), heilt der
    // naechste Umzug nicht mehr still mit — sie wird gemeldet und per Fix-SQL
    // bereinigt. Dieselbe Linie wie beim Reversal (Codex-Review R3).
    const fremderStand = zuErsetzen.filter((z) =>
        !(Math.abs(Number(z.quantity) - mengeVorher) <= MENGEN_TOLERANZ)
        || String(z.service_id ?? '').trim() !== positionVorher);
    if (fremderStand.length > 0) {
        const grund = `${fremderStand.length} lebende Journalzeile(n) tragen nicht den Stand vor dem Umzug `
            + `(${mengeVorher} auf ${positionVorher}): `
            + fremderStand.map((z) => `${z.id} = ${z.quantity} auf ${z.service_id}`).join(', ');
        console.warn(
            `[backfillMoveJournal] Umgezogene Zeile NICHT im Journal ersetzt: das Journal weicht ab — ${grund}. `
            + 'Kein Aufruf, kein Rückfall; die Abweichung zeigt der Nachtlauf.',
        );
        void meldeSpiegelFehler(projectId, zeilen, new Error(`Zuordnungsumzug nicht gespiegelt: ${grund}`), 'Ersetzen');
        return uebersprungen(grund);
    }

    const ersetzt = await ersetzeJournalzeilen(
        projectId,
        zuErsetzen.map((z) => ({ id: z.id, quantity: mengeVorher })),
        'correction',
        zeilen,
    );
    if (ersetzt.ok) return { art: 'ersetzt' };

    // ── 55000: KEINE Wiederholung, KEIN Rueckfall ───────────────────────────
    // Wie beim Reversal (Codex-Review R2): ein anderer Aufruf hat den Stand
    // geaendert, eine Wiederholung schriebe womoeglich einen ueberholten.
    // Dazu der Fall dieses Pfads: A→B→A→B — der Ersatzschluessel des dritten
    // Umzugs ist der des ersten und laengst stillgelegt, Un-void gibt es
    // nicht. Die Funktion lehnt ab, die alte Zeile bleibt lebend: kein
    // Verlust, nur der falsche Ort, und den zeigt der Zuordnungs-Schatten im
    // Nachtlauf. Gemeldet hat `ersetzeJournalzeilen` selbst.
    if (istZustandGeaendert(ersetzt.error)) {
        console.warn(
            `[backfillMoveJournal] Ersetzen der umgezogenen Zeile ${id} abgelehnt (55000): der Stand im Journal passt nicht `
            + '(ein anderer Aufruf, oder der Ersatzschlüssel ist nach A→B→A→B schon stillgelegt). Absichtlich KEINE Wiederholung '
            + 'und kein Rückfall — die alte Zeile bleibt lebend, der Nachtlauf zeigt sie.',
            ersetzt.error,
        );
        return uebersprungen('Ersetzen abgelehnt (55000)');
    }
    if (ersetzt.reason !== 'rpc_fehlt') {
        // Kein Rueckfall auf den Zweischritt: er schriebe genau den
        // Halbzustand, den die Funktion gerade verhindert hat.
        console.warn(
            `[backfillMoveJournal] Ersetzen der umgezogenen Zeile ${id} im Journal gescheitert — nichts geschrieben, `
            + 'die alte Zeile bleibt lebend auf der Quellposition.',
            ersetzt.error,
        );
        return uebersprungen(`Ersetzen gescheitert (${ersetzt.reason ?? 'unbekannt'})`);
    }

    console.warn(
        '[backfillMoveJournal] service_journal_ersetzen (Nr. 136) fehlt — Rückfall auf „anhängen, dann stilllegen“. '
        + 'Bei einer Berichtszeile scheitert das Anhängen am Eindeutigkeitsriegel (23505), die alte Zeile bleibt lebend.',
    );
    return anhaengenDannStilllegen({ ...args, id, neu, neueId, stillzulegen: zuErsetzen.map((z) => z.id), zeilen });
};

/**
 * Der RUECKFALL ohne Nr. 136 — anhaengen ZUERST, stilllegen DANACH, ohne Korb
 * (Begruendung wie `serviceReversalService.anhaengenDannStilllegen`:
 * scheitert das Anhaengen, bleibt die alte Zeile allein und lebend).
 *
 * Eine Pruefung mehr als dort: VOR dem Stilllegen muss die Ersatzzeile
 * wirklich LEBEN. `written` heisst nur „die RPC hat nicht geworfen" — bei
 * A→B→A→B traegt die Ersatzzeile einen schon stillgelegten Schluessel,
 * `on conflict do nothing` schreibt nichts und meldet trotzdem Erfolg. Danach
 * die alte stillzulegen waere genau Fall 6263: KEINE lebende Zeile. Der
 * zusaetzliche Lesevorgang kostet nur auf diesem seltenen Weg.
 */
const anhaengenDannStilllegen = async (args: ZuordnungsUmzugSpiegeln & {
    id: string;
    neu: ServiceTransaction;
    neueId: string;
    stillzulegen: string[];
    zeilen: ServiceJournalRow[];
}): Promise<Ausgang> => {
    const { projectId, vorher, linkedProjectConfig, id, neu, neueId, stillzulegen, zeilen } = args;
    const angehaengt = await mirrorEntriesToJournalOhneKorb({
        projectId,
        entries: [neu],
        previousLog: vorher,
        source: 'admin_accounting',
        linkedProjectConfig,
    });
    if (!angehaengt.written) {
        console.warn(
            `[backfillMoveJournal] Umgezogene Zeile ${id} nicht ins Journal geschrieben — die alte bleibt lebend auf der Quellposition.`,
            angehaengt.reason,
        );
        return uebersprungen(`Rückfall: Anhängen gescheitert (${angehaengt.reason ?? 'unbekannt'})`);
    }

    // FRISCH gelesen, nicht aus dem Stand vor dem Spiegeln: nur das Journal
    // NACH dem Anhaengen sagt, ob die Ersatzzeile lebt.
    const danach = await ladeLebendeJournalzeilenZu(projectId, id);
    if (!danach.journalGelesen) {
        // Angehaengt ist, stillgelegt wird nicht: zwei lebende Zeilen
        // (`mehrfach_lebend`) sind sichtbar, eine faelschlich stillgelegte
        // waere still — die schlechtere Richtung.
        console.warn(
            `[backfillMoveJournal] Rückfall: Journal nach dem Anhängen nicht lesbar — alte Journalzeile(n) von ${id} NICHT stillgelegt. `
            + 'Zwei lebende Zeilen zu derselben id sind `mehrfach_lebend` (Nr. 115), der Nachtlauf zeigt sie.',
        );
        return { art: 'rueckfall' };
    }
    if (!danach.zeilen.some((z) => z.id === neueId)) {
        const grund = `die Ersatzzeile ${neueId} lebt nach dem Anhängen nicht (Schlüssel schon stillgelegt, A→B→A→B)`;
        console.warn(
            `[backfillMoveJournal] Rückfall: alte Journalzeile(n) von ${id} NICHT stillgelegt — ${grund}. `
            + 'Stilllegen hinterließe keine lebende Zeile (Fall 6263); die alte bleibt auf der Quellposition, der Nachtlauf zeigt sie.',
        );
        void meldeSpiegelFehler(projectId, zeilen, new Error(`Zuordnungsumzug nicht gespiegelt: ${grund}`), 'Ersetzen');
        return uebersprungen(`Rückfall: ${grund}`);
    }

    const stillgelegt = await voidJournalEntries(projectId, stillzulegen, 'correction', { nurJournalIds: true });
    // Bleibt eine alte Zeile lebend, stehen zwei Zeilen zu derselben JSON-id im
    // Journal — `mehrfach_lebend` (Nr. 115). Kein Geld, solange das JSON fuehrt.
    if (stillgelegt.error || stillgelegt.voided < stillzulegen.length) {
        console.warn(
            `[backfillMoveJournal] Die alte Journalzeile der umgezogenen Zeile ${id} wurde nicht (vollständig) stillgelegt — `
            + `zwei lebende Zeilen zu derselben id sind \`mehrfach_lebend\` (Nr. 115). Erwartet ${stillzulegen.length}, stillgelegt ${stillgelegt.voided}.`,
            stillgelegt.error ?? stillgelegt.unbekannt,
        );
    }
    return { art: 'rueckfall' };
};
